import { useEffect, useState } from "react";
import { Megaphone, RefreshCw, Save } from "lucide-react";
import { LocaleCombobox } from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import {
  getTelephonySettings,
  saveTelephonySettings,
  NOTICE_LOCALES,
  type NoticeDirectionSettings,
  type NoticeLocale,
  type NoticeLocaleOverlay,
  type NoticeSlot,
  type NoticeVariant,
  type TelephonyStatus,
} from "../../../../lib/calls";
import { Btn, PillTabs, StatusPill } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";
import { NoticeDirectionFields } from "./NoticeDirectionFields";
import { NoticeLocaleFields } from "./NoticeLocaleFields";
import {
  BASE_TAB,
  localeHasWording,
  localeInUse,
  noticeLocalePatch,
  noticeTabs,
  withLocaleEnabled,
  withOverlayEdit,
  type NoticeTab,
} from "./noticeLocales";

const EMPTY_DIRECTION: NoticeDirectionSettings = {
  enabled: true,
  recorded: { audioUrl: "", text: "" },
  processed: { audioUrl: "", text: "" },
};

/**
 * Settings -> Integrations -> the spoken notice.
 *
 * Its own tab rather than a block under Telephony, because it is not a piece of
 * the Twilio setup: it is the sentence a customer hears before anything of ours
 * starts reading their call, and it is the switch the co-pilot cannot run
 * without. Sitting directly above the co-pilot tab is the point - "why is the
 * panel off" and "what does the caller hear" are one answer.
 *
 * Two announcements per direction, not one, because there are two different
 * true statements. A recorded call is recorded and later transcribed. A call
 * with recording off and the co-pilot on is not recorded at all, and its audio
 * is still read by a model while it happens - a notice that only mentioned
 * recording would name the one thing that is not occurring.
 *
 * And two DIRECTIONS, each with its own switch and its own pair, because a cold
 * call we place and a call somebody makes to us are not the same conversation.
 * One switch for both meant "do not open my sales calls with a machine talking"
 * could only be said by also going silent on the people who ring us.
 */
export function NoticeCard() {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [language, setLanguage] = useState("et-EE");
  const [voice, setVoice] = useState("");
  const [outbound, setOutbound] = useState<NoticeDirectionSettings>(EMPTY_DIRECTION);
  const [inbound, setInbound] = useState<NoticeDirectionSettings>(EMPTY_DIRECTION);
  const [locales, setLocales] = useState<Partial<Record<NoticeLocale, NoticeLocaleOverlay>>>({});
  const [tab, setTab] = useState<NoticeTab>(BASE_TAB);
  /**
   * Which language tabs this form is allowed to write.
   *
   * Kept rather than derived, because "the operator opened Russian and cleared
   * it" and "the operator never opened Russian" produce identical state and
   * must produce different saves. A form that posted every language on every
   * save would take what it loaded and write it over what a colleague saved in
   * another tab since - see `noticeLocalePatch`.
   */
  const [touched, setTouched] = useState<Set<NoticeLocale>>(new Set());

  function hydrate(data: TelephonyStatus) {
    setStatus(data);
    setEnabled(data.notice.enabled);
    setLanguage(data.notice.language);
    setVoice(data.notice.voice);
    setOutbound(data.notice.outbound);
    setInbound(data.notice.inbound);
    setLocales(data.notice.locales ?? {});
    // A completed save is a clean slate: what is on screen now IS what the
    // server holds, so nothing is pending and the next save must not re-post a
    // language the operator has stopped editing.
    setTouched(new Set());
  }

  useEffect(() => {
    getTelephonySettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    const localePatch = noticeLocalePatch(locales, touched);
    try {
      hydrate(
        await saveTelephonySettings({
          notice: {
            enabled,
            language,
            voice,
            outbound,
            inbound,
            // Omitted entirely when no language tab was touched. `undefined`
            // reads as "this form said nothing about languages"; `{}` would
            // read as "there are none", and on a merge this deep the difference
            // is somebody's afternoon.
            ...(localePatch ? { locales: localePatch } : {}),
          },
        }),
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  function editLocale(slot: NoticeSlot, next: NoticeVariant) {
    if (tab === BASE_TAB) return;
    const locale = tab;
    setLocales((prev) => withOverlayEdit(prev, locale, slot, next));
    setTouched((prev) => (prev.has(locale) ? prev : new Set(prev).add(locale)));
  }

  if (loading) return <PanelLoader />;

  const record = status?.twilio.record ?? false;
  const pilotOn = status?.pilot.enabled ?? false;
  // Green when the calls this CRM PLACES carry an announcement. Inbound has its
  // own line below rather than its own badge: one status word cannot be two
  // answers, and the outbound one is what the dial pad reads.
  const inboundAnswered = status?.inbound.ready ?? false;

  return (
    // The voice pickers below are `Pop` menus, and Settings mounts no `PopHost`
    // of its own - a `Pop` without one is a control that silently does not open.
    // Mounted here rather than inside each row so all four announcements share
    // one open slot: opening the second picker closes the first.
    <PopHost>
    <IntegrationCard
      icon={Megaphone}
      title={t("settings.notice.title")}
      desc={t("settings.notice.desc")}
      status={
        <ConnStatus
          ok={status?.notice.outboundPlays ?? false}
          okLabel={t("settings.notice.statusOn")}
          offLabel={t("settings.notice.statusOff")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        <Note tone="info">{t("settings.notice.about")}</Note>

        {/* The address the announcement points at (§3.11). Eight seconds cannot
            carry the controller, the lawful basis, the retention period and the
            way to object, so the wording above has somewhere to send people -
            and the person writing that wording needs the address while they are
            writing it, not three tabs away. Read from the server rather than
            assembled here: the short path exists to be said into a telephone,
            so it will get shorter again. */}
        {status?.privacyNoticeUrl ? (
          <Note tone="info">
            {t("settings.notice.privacy")}{" "}
            <a
              href={status.privacyNoticeUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-2"
            >
              {status.privacyNoticeUrl.replace(/^https?:\/\//, "")}
            </a>
          </Note>
        ) : null}

        {/* The one combination that is a hard stop rather than a preference: the
            co-pilot is switched on and nothing would be said. Stated here as
            well as in the call window, because this is the page that fixes it. */}
        {pilotOn && !status?.pilot.ready && status?.pilot.blocker === "no_notice" ? (
          <Note tone="bad">{t("settings.notice.blocksPilot")}</Note>
        ) : null}

        {/* The same silence with nothing AI about it: recording is on and this
            page is what would say so. True whether or not the co-pilot is
            switched on, which is why it is a second line rather than a clause
            in the one above. */}
        {status?.recordsWithoutNotice ? (
          <Note tone="bad">{t("settings.notice.recordsWithoutNotice")}</Note>
        ) : null}

        {/* Answering the phone and announcing nothing on it. Only worth saying
            while inbound calls are actually being taken - on an installation
            that refuses them, a silent inbound announcement is a setting for a
            situation that does not arise. */}
        {inboundAnswered && !status?.notice.inboundPlays ? (
          <Note tone="warn">{t("settings.notice.inboundSilent")}</Note>
        ) : null}

        {/* Twilio has no Estonian voice, so the pre-written Estonian sentences
            would come back read by an English one. Better said here than
            discovered on a customer's phone. */}
        {status?.notice.textNeedsClip ? (
          <Note tone="warn">{t("settings.notice.needsClip", { language })}</Note>
        ) : null}

        <ToggleRow
          label={t("settings.notice.enable")}
          hint={t("settings.notice.enableHint")}
          checked={enabled}
          onChange={setEnabled}
        />

        {/* One strip, all three languages, always. A tab that only appeared
            once a translation existed would leave an operator hunting for
            where to write the first one. The dot after a name is what says a
            language has wording; the tab being there is not a claim that it is
            finished.

            `PillTabs` rather than a hand-rolled row: the selected pill is the
            same "this is where you are" signal the sidebar uses, and a second
            highlight invented here would be a second thing to learn. */}
        <PillTabs
          small
          value={tab}
          onChange={setTab}
          tabs={noticeTabs(NOTICE_LOCALES).map((key) => ({
            value: key,
            label:
              key === BASE_TAB
                ? t("settings.notice.baseTab")
                : // A dot for "there is work on this tab", and a pause mark for
                  // "and none of it reaches a call". Two marks rather than one,
                  // because a paused language and an empty one are different
                  // states and the whole point of the switch is telling them
                  // apart without opening the tab.
                  `${t(`settings.notice.lang.${key}`)}` +
                  (localeHasWording(locales[key]) ? " \u2022" : "") +
                  (localeHasWording(locales[key]) && !localeInUse(locales[key]) ? " \u23f8" : ""),
          }))}
        />

        {tab === BASE_TAB ? (
          <>
            <NoticeDirectionFields
              direction="outbound"
              value={outbound}
              onChange={setOutbound}
              record={record}
              clips={status?.clips ?? {}}
              builtInAudio={status?.builtInAudio ?? {}}
            />

            <NoticeDirectionFields
              direction="inbound"
              value={inbound}
              onChange={setInbound}
              record={record}
              clips={status?.clips ?? {}}
              builtInAudio={status?.builtInAudio ?? {}}
            />
          </>
        ) : (
          <NoticeLocaleFields
            locale={tab}
            overlay={locales[tab]}
            /* All nine, not the two this started with. The greyed base
               sentence above a translation is the only way to check that the
               two still say the same thing, and for seven of the nine it was
               simply absent - the tab printed the translation of a sentence it
               would not show. The four that live on other cards are read from
               the loaded status rather than from this form, because this form
               does not edit them and a blank line would read as "the base is
               empty" rather than as "not edited here". */
            base={{
              "notice.outbound.recorded": outbound.recorded,
              "notice.outbound.processed": outbound.processed,
              "notice.inbound.recorded": inbound.recorded,
              "notice.inbound.processed": inbound.processed,
              ...(status
                ? {
                    "inbound.voicemail": status.inbound.voicemailPrompt,
                    "inbound.unavailable": status.inbound.unavailablePrompt,
                    "hours.voicemail": status.inbound.hours.voicemailPrompt,
                    "hours.closed": status.inbound.hours.closedPrompt,
                    "menu.department": status.inbound.menu.departmentPrompt,
                  }
                : {}),
            }}
            onChange={editLocale}
            enabled={locales[tab]?.enabled !== false}
            onEnabledChange={(next) => {
              setLocales((prev) => withLocaleEnabled(prev, tab, next));
              setTouched((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
            }}
            record={record}
            clips={status?.clips ?? {}}
            needsClip={status?.notice.localeNeedsClip?.[tab] ?? false}
          />
        )}

        <FieldGroup title={t("settings.notice.voiceGroup")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.notice.language")} hint={t("settings.notice.languageHint")}>
              {/* The tags Twilio has a voice for, plus whatever is stored or typed:
                  Estonian has no Twilio voice and is still the right tag for a
                  recorded clip, so the list must never refuse a code. */}
              <LocaleCombobox
                className="h-11"
                locales={status?.notice.sayLanguages ?? []}
                displayLocale={uiLocale}
                value={language}
                onChange={setLanguage}
                onCreate={(code) => setLanguage(code.trim())}
                createLabel={(code) => t("common.localeUseCode", { code: code.trim() })}
                placeholder="et-EE"
                aria-label={t("settings.notice.language")}
                searchPlaceholder={t("common.localeSearch")}
                emptyLabel={t("common.localeNone")}
              />
            </Field>
            <Field label={t("settings.notice.voice")} hint={t("settings.notice.voiceHint")}>
              {/* A voice that really exists. A made-up placeholder in a field
                  whose whole job is naming a Twilio voice would be a wrong
                  answer typed for the operator. */}
              <Pill value={voice} onChange={setVoice} placeholder="Polly.Amy" autoComplete="off" mono />
            </Field>
          </div>
        </FieldGroup>

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.notice.saving") : t("settings.notice.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.notice.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.notice.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
    </PopHost>
  );
}
